import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  ColorInput,
  Group,
  Loader,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconArrowDown,
  IconArrowLeft,
  IconArrowUp,
  IconEye,
  IconPencil,
  IconPlus,
  IconPrinter,
  IconShare,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import type { MessageKey } from "../../../shared/i18n";
import { getTemplate, TEMPLATES } from "../../../shared/templates";
import {
  endpoints,
  type ResumeDocument,
  type ResumeLayout,
  type ResumeProfile,
  type ResumeTheme,
  type SectionKey,
  uploadAsset,
} from "../../api/client";
import { documentQuery, useNotifyError } from "../../api/queries";
import { useAuth } from "../../auth/provider";
import { ShareDialog } from "../../components/share-dialog";
import { ThemeToggle } from "../../components/theme-toggle";
import { CvRenderer } from "../../cv/renderer";
import { useMessages } from "../../i18n/provider";
import { AddItemDialog } from "./add-item-dialog";
import styles from "./editor.module.css";
import {
  AwardEditor,
  CertificationEditor,
  EducationEditor,
  ExperienceEditor,
  LanguageEditor,
  LinkEditor,
  ProfileEditor,
  ProjectEditor,
  SkillEditor,
} from "./section-editors";

type SaveStatus = "saved" | "saving" | "error";

const PAGE_WIDTH = 793.7;
const AUTOSAVE_MS = 800;

const ACCENTS = [
  "#1d1d1f",
  "#0071e3",
  "#0b7285",
  "#1f5c4a",
  "#8a1c1c",
  "#d70015",
  "#6b4eed",
  "#b25e00",
];

function swapAt<T>(list: T[], left: number, right: number): T[] {
  const next = [...list];
  const first = next[left];
  const second = next[right];
  if (first === undefined || second === undefined) return next;
  next[left] = second;
  next[right] = first;
  return next;
}

const SECTION_ADD: Record<SectionKey, MessageKey> = {
  experience: "editor.addExperience",
  education: "editor.addEducation",
  projects: "editor.addProject",
  skills: "editor.addSkill",
  languages: "editor.addLanguage",
  certifications: "editor.addCertification",
  awards: "editor.addAward",
  links: "editor.addLink",
};

const SECTION_EMPTY: Record<SectionKey, MessageKey> = {
  experience: "editor.emptyExperience",
  education: "editor.emptyEducation",
  projects: "editor.emptyProjects",
  skills: "editor.emptySkills",
  languages: "editor.emptyLanguages",
  certifications: "editor.emptyCertifications",
  awards: "editor.emptyAwards",
  links: "editor.emptyLinks",
};

const SECTION_LABEL: Record<string, MessageKey> = {
  profile: "editor.profile",
  experience: "editor.experience",
  education: "editor.education",
  projects: "editor.projects",
  skills: "editor.skills",
  languages: "editor.languages",
  certifications: "editor.certifications",
  awards: "editor.awards",
  links: "editor.links",
};

export function EditorPage() {
  const { id = "" } = useParams();
  const t = useMessages();
  const { me } = useAuth();
  const notifyError = useNotifyError();

  const {
    data: fetched,
    isLoading,
    error,
  } = useQuery({
    ...documentQuery(id),
    enabled: Boolean(id),
  });

  const [doc, setDoc] = useState<ResumeDocument | null>(null);
  const docRef = useRef<ResumeDocument | null>(null);
  const loadedId = useRef<string | null>(null);
  const [status, setStatus] = useState<SaveStatus>("saved");
  const [adding, setAdding] = useState<SectionKey | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const timers = useRef(new Map<string, number>());
  const pending = useRef(new Map<string, () => Promise<unknown>>());

  useEffect(() => {
    if (!fetched || loadedId.current === fetched.id) return;
    loadedId.current = fetched.id;
    docRef.current = fetched;
    setDoc(fetched);
  }, [fetched]);

  const runSave = useCallback(async (key: string) => {
    const task = pending.current.get(key);
    if (!task) return;
    pending.current.delete(key);
    const timer = timers.current.get(key);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(key);
    }
    setStatus("saving");
    try {
      await task();
      setStatus(pending.current.size > 0 ? "saving" : "saved");
    } catch (saveError) {
      pending.current.set(key, task);
      setStatus("error");
      if (saveError instanceof Error && !(saveError instanceof TypeError)) {
        // Keep the local edit; the retry affordance sits next to the status.
      }
    }
  }, []);

  const schedule = useCallback(
    (key: string, task: () => Promise<unknown>) => {
      pending.current.set(key, task);
      const existing = timers.current.get(key);
      if (existing) clearTimeout(existing);
      setStatus("saving");
      timers.current.set(
        key,
        window.setTimeout(() => {
          void runSave(key);
        }, AUTOSAVE_MS),
      );
    },
    [runSave],
  );

  const retry = useCallback(() => {
    for (const key of [...pending.current.keys()]) void runSave(key);
  }, [runSave]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (pending.current.size === 0) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      for (const [key, timer] of timers.current) {
        clearTimeout(timer);
        const task = pending.current.get(key);
        if (task) void task();
      }
    };
  }, []);

  const applyDoc = useCallback((updater: (current: ResumeDocument) => ResumeDocument) => {
    setDoc((current) => {
      if (!current) return current;
      const next = updater(current);
      docRef.current = next;
      return next;
    });
  }, []);

  const patchProfile = useCallback(
    (patch: Partial<ResumeProfile>) => {
      const merged = { ...(docRef.current?.profile as ResumeProfile), ...patch };
      applyDoc((current) => ({ ...current, profile: merged }));
      const body = {
        fullName: merged.fullName,
        headline: merged.headline,
        email: merged.email,
        phone: merged.phone,
        location: merged.location,
        summary: merged.summary,
        showEmail: merged.showEmail,
        showPhone: merged.showPhone,
      };
      schedule("profile", () => endpoints.updateProfile(id, body));
    },
    [applyDoc, id, schedule],
  );

  const patchTheme = useCallback(
    (patch: Partial<ResumeTheme>) => {
      const current = docRef.current;
      if (!current) return;
      const merged = { ...current.theme, ...patch };
      applyDoc((value) => ({ ...value, theme: merged }));
      schedule("theme", () => endpoints.updateTheme(id, patch));
    },
    [applyDoc, id, schedule],
  );

  const patchLayout = useCallback(
    (patch: Partial<ResumeLayout>) => {
      const current = docRef.current;
      if (!current) return;
      const merged = { ...current.layout, ...patch };
      applyDoc((value) => ({ ...value, layout: merged }));
      schedule("layout", () => endpoints.updateLayout(id, patch));
    },
    [applyDoc, id, schedule],
  );

  const patchItem = useCallback(
    (section: SectionKey, itemId: string, patch: Record<string, unknown>) => {
      applyDoc((current) => {
        const list = current[section] as unknown as { id: string }[];
        return {
          ...current,
          [section]: list.map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
        } as ResumeDocument;
      });
      schedule(`${section}:${itemId}`, () =>
        endpoints.updateSectionItem(id, section, itemId, patch),
      );
    },
    [applyDoc, id, schedule],
  );

  const moveItem = useCallback(
    (section: SectionKey, index: number, direction: -1 | 1) => {
      const current = docRef.current;
      if (!current) return;
      const currentList = current[section] as unknown as { id: string }[];
      const target = index + direction;
      if (target < 0 || target >= currentList.length) return;
      const list = swapAt(currentList, index, target);
      applyDoc((value) => ({ ...value, [section]: list }) as ResumeDocument);
      schedule(`order:${section}`, () =>
        endpoints.reorderSection(
          id,
          section,
          list.map((item) => item.id),
        ),
      );
    },
    [applyDoc, id, schedule],
  );

  const removeItem = useCallback(
    async (section: SectionKey, itemId: string) => {
      setStatus("saving");
      try {
        await endpoints.deleteSectionItem(id, section, itemId);
        applyDoc((current) => {
          const list = current[section] as unknown as { id: string }[];
          return {
            ...current,
            [section]: list.filter((item) => item.id !== itemId),
          } as ResumeDocument;
        });
        setStatus(pending.current.size > 0 ? "saving" : "saved");
      } catch (removeError) {
        setStatus("error");
        notifyError(removeError);
      }
    },
    [applyDoc, id, notifyError],
  );

  const addItem = useCallback(
    async (section: SectionKey, input: Record<string, unknown>) => {
      const result = await endpoints.createSectionItem<{ id: string }>(id, section, input);
      applyDoc(
        (current) =>
          ({
            ...current,
            [section]: [...(current[section] as unknown as { id: string }[]), result.item],
          }) as ResumeDocument,
      );
      setStatus("saved");
    },
    [applyDoc, id],
  );

  const changeTemplate = (value: string | null) => {
    if (!value) return;
    const meta = getTemplate(value);
    if (!meta) return;
    if (meta.tier === "pro" && !me?.limits.allTemplates) {
      notifications.show({
        color: "gray",
        message: t("templates.proRequired"),
        autoClose: 3500,
      });
      return;
    }
    applyDoc((current) => ({ ...current, templateId: value }));
    schedule("template", () => endpoints.updateResume(id, { templateId: value }));
  };

  const changeTitle = (value: string) => {
    applyDoc((current) => ({ ...current, title: value }));
    schedule("title", () => endpoints.updateResume(id, { title: value }));
  };

  const uploadAvatar = async (file: File) => {
    setUploadingAvatar(true);
    setStatus("saving");
    try {
      const asset = await uploadAsset(file, "avatar");
      await endpoints.updateProfile(id, { avatarAssetId: asset.id });
      applyDoc((current) => ({
        ...current,
        profile: { ...current.profile, avatarUrl: asset.url },
      }));
      setStatus("saved");
    } catch (uploadError) {
      setStatus("error");
      notifyError(uploadError);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const uploadProjectImage = async (itemId: string, file: File) => {
    setStatus("saving");
    try {
      const asset = await uploadAsset(file, "project");
      await endpoints.updateSectionItem(id, "projects", itemId, {
        imageAssetId: asset.id,
      });
      applyDoc((current) => ({
        ...current,
        projects: current.projects.map((item) =>
          item.id === itemId ? { ...item, imageUrl: asset.url } : item,
        ),
      }));
      setStatus("saved");
    } catch (uploadError) {
      setStatus("error");
      notifyError(uploadError);
    }
  };

  const templateChoices = useMemo(
    () =>
      TEMPLATES.map((meta) => ({
        value: meta.id,
        label:
          meta.tier === "pro" && !me?.limits.allTemplates
            ? `${t(meta.nameKey as MessageKey)} · ${t("common.pro")}`
            : t(meta.nameKey as MessageKey),
      })),
    [me?.limits.allTemplates, t],
  );

  if (isLoading) {
    return (
      <Centered>
        <Loader size="sm" color="gray" />
      </Centered>
    );
  }

  if (error || !doc) {
    return (
      <Centered>
        <Stack align="center" gap="sm">
          <Text c="dimmed">{t("errors.notFound")}</Text>
          <Button component={Link} to="/dashboard" variant="light">
            {t("common.back")}
          </Button>
        </Stack>
      </Centered>
    );
  }

  const hidden = new Set(doc.layout.hiddenSections);

  const print = () => {
    setPreviewOpen(true);
    window.setTimeout(() => window.print(), 200);
  };

  return (
    <div className={styles.root}>
      <header className={styles.topbar}>
        <div className={styles.topbarInner}>
          <Tooltip label={t("common.back")} withArrow>
            <ActionIcon
              component={Link}
              to="/dashboard"
              variant="subtle"
              color="gray"
              size="lg"
              aria-label={t("common.back")}
            >
              <IconArrowLeft size={20} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <TextInput
            variant="unstyled"
            value={doc.title}
            onChange={(event) => changeTitle(event.currentTarget.value)}
            aria-label={t("editor.titlePlaceholder")}
            placeholder={t("editor.titlePlaceholder")}
            maxLength={120}
            className={styles.titleInput}
          />
          <SaveState status={status} onRetry={retry} />
          <Group gap={8} className={styles.topActions}>
            <ThemeToggle />
            <Button
              variant={previewOpen ? "default" : "filled"}
              size="sm"
              aria-label={previewOpen ? t("editor.formTab") : t("editor.previewTab")}
              aria-pressed={previewOpen}
              leftSection={
                previewOpen ? (
                  <IconPencil size={16} stroke={1.5} />
                ) : (
                  <IconEye size={16} stroke={1.5} />
                )
              }
              onClick={() => setPreviewOpen((value) => !value)}
            >
              <span className={styles.actionLabel}>
                {previewOpen ? t("editor.formTab") : t("editor.previewTab")}
              </span>
            </Button>
            <Button
              variant="default"
              size="sm"
              aria-label={t("common.share")}
              leftSection={<IconShare size={16} stroke={1.5} />}
              onClick={() => setShareOpen(true)}
            >
              <span className={styles.actionLabel}>{t("common.share")}</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              aria-label={t("common.print")}
              leftSection={<IconPrinter size={16} stroke={1.5} />}
              onClick={print}
            >
              <span className={styles.actionLabel}>{t("common.print")}</span>
            </Button>
          </Group>
        </div>
      </header>

      <div className={styles.body} data-preview={previewOpen ? "true" : undefined}>
        <div className={styles.formCol}>
          <Panel title={t("editor.profile")}>
            <ProfileEditor
              profile={doc.profile}
              onPatch={patchProfile}
              onUploadAvatar={(file) => void uploadAvatar(file)}
              uploading={uploadingAvatar}
            />
          </Panel>

          {(Object.keys(SECTION_ADD) as SectionKey[]).map((section) => {
            const items = doc[section] as unknown as { id: string }[];
            return (
              <Panel
                key={section}
                title={t(SECTION_LABEL[section] as MessageKey)}
                count={items.length}
                onAdd={() => setAdding(section)}
                addLabel={t(SECTION_ADD[section])}
              >
                {items.length === 0 ? (
                  <div className={styles.empty}>
                    <Text size="sm" c="dimmed">
                      {t(SECTION_EMPTY[section])}
                    </Text>
                    <Button
                      size="compact-sm"
                      variant="light"
                      leftSection={<IconPlus size={14} stroke={1.5} />}
                      onClick={() => setAdding(section)}
                    >
                      {t(SECTION_ADD[section])}
                    </Button>
                  </div>
                ) : (
                  <Stack gap="sm">
                    {section === "experience"
                      ? doc.experience.map((item, index) => (
                          <ExperienceEditor
                            key={item.id}
                            item={item}
                            index={index}
                            count={doc.experience.length}
                            onPatch={(patch) => patchItem(section, item.id, patch)}
                            onDelete={() => void removeItem(section, item.id)}
                            onMove={(direction) => moveItem(section, index, direction)}
                          />
                        ))
                      : null}
                    {section === "education"
                      ? doc.education.map((item, index) => (
                          <EducationEditor
                            key={item.id}
                            item={item}
                            index={index}
                            count={doc.education.length}
                            onPatch={(patch) => patchItem(section, item.id, patch)}
                            onDelete={() => void removeItem(section, item.id)}
                            onMove={(direction) => moveItem(section, index, direction)}
                          />
                        ))
                      : null}
                    {section === "projects"
                      ? doc.projects.map((item, index) => (
                          <ProjectEditor
                            key={item.id}
                            item={item}
                            index={index}
                            count={doc.projects.length}
                            onPatch={(patch) => patchItem(section, item.id, patch)}
                            onDelete={() => void removeItem(section, item.id)}
                            onMove={(direction) => moveItem(section, index, direction)}
                            onUploadImage={(file) => void uploadProjectImage(item.id, file)}
                          />
                        ))
                      : null}
                    {section === "skills"
                      ? doc.skills.map((item, index) => (
                          <SkillEditor
                            key={item.id}
                            item={item}
                            index={index}
                            count={doc.skills.length}
                            onPatch={(patch) => patchItem(section, item.id, patch)}
                            onDelete={() => void removeItem(section, item.id)}
                            onMove={(direction) => moveItem(section, index, direction)}
                          />
                        ))
                      : null}
                    {section === "languages"
                      ? doc.languages.map((item, index) => (
                          <LanguageEditor
                            key={item.id}
                            item={item}
                            index={index}
                            count={doc.languages.length}
                            onPatch={(patch) => patchItem(section, item.id, patch)}
                            onDelete={() => void removeItem(section, item.id)}
                            onMove={(direction) => moveItem(section, index, direction)}
                          />
                        ))
                      : null}
                    {section === "certifications"
                      ? doc.certifications.map((item, index) => (
                          <CertificationEditor
                            key={item.id}
                            item={item}
                            index={index}
                            count={doc.certifications.length}
                            onPatch={(patch) => patchItem(section, item.id, patch)}
                            onDelete={() => void removeItem(section, item.id)}
                            onMove={(direction) => moveItem(section, index, direction)}
                          />
                        ))
                      : null}
                    {section === "awards"
                      ? doc.awards.map((item, index) => (
                          <AwardEditor
                            key={item.id}
                            item={item}
                            index={index}
                            count={doc.awards.length}
                            onPatch={(patch) => patchItem(section, item.id, patch)}
                            onDelete={() => void removeItem(section, item.id)}
                            onMove={(direction) => moveItem(section, index, direction)}
                          />
                        ))
                      : null}
                    {section === "links"
                      ? doc.links.map((item, index) => (
                          <LinkEditor
                            key={item.id}
                            item={item}
                            index={index}
                            count={doc.links.length}
                            onPatch={(patch) => patchItem(section, item.id, patch)}
                            onDelete={() => void removeItem(section, item.id)}
                            onMove={(direction) => moveItem(section, index, direction)}
                          />
                        ))
                      : null}
                  </Stack>
                )}
              </Panel>
            );
          })}

          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <span className={styles.panelTitle}>{t("editor.sectionsTitle")}</span>
            </div>
            <div className={styles.sectionList}>
              {doc.layout.sectionOrder.map((key, index) => (
                <div key={key} className={styles.sectionRow}>
                  <Text size="sm">{t(SECTION_LABEL[key] as MessageKey)}</Text>
                  <Group gap={4}>
                    <Switch
                      size="xs"
                      checked={!hidden.has(key)}
                      aria-label={
                        hidden.has(key) ? t("editor.showSection") : t("editor.hideSection")
                      }
                      onChange={(event) => {
                        const next = new Set(doc.layout.hiddenSections);
                        if (event.currentTarget.checked) next.delete(key);
                        else next.add(key);
                        patchLayout({
                          hiddenSections: [...next] as ResumeDocument["layout"]["hiddenSections"],
                        });
                      }}
                    />
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      disabled={index === 0}
                      aria-label={t("common.moveUp")}
                      onClick={() => {
                        patchLayout({
                          sectionOrder: swapAt(doc.layout.sectionOrder, index - 1, index),
                        });
                      }}
                    >
                      <IconArrowUp size={14} stroke={1.5} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      disabled={index === doc.layout.sectionOrder.length - 1}
                      aria-label={t("common.moveDown")}
                      onClick={() => {
                        patchLayout({
                          sectionOrder: swapAt(doc.layout.sectionOrder, index, index + 1),
                        });
                      }}
                    >
                      <IconArrowDown size={14} stroke={1.5} />
                    </ActionIcon>
                  </Group>
                </div>
              ))}
            </div>
          </section>
        </div>

        {previewOpen ? (
          <div className={styles.previewCol}>
            <section className={styles.design}>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={600}>
                  {t("nav.templates")}
                </Text>
                <Anchor component={Link} to="/templates" size="xs">
                  {t("landing.viewAll")}
                </Anchor>
              </Group>
              <Select
                data={templateChoices}
                value={doc.templateId}
                onChange={changeTemplate}
                allowDeselect={false}
                size="sm"
                aria-label={t("nav.templates")}
              />
              <div className={styles.swatches}>
                {ACCENTS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={styles.swatch}
                    style={{ background: color }}
                    aria-label={color}
                    aria-pressed={doc.theme.accent === color}
                    onClick={() => patchTheme({ accent: color })}
                  />
                ))}
              </div>
              <SimpleGrid cols={2} spacing="xs">
                <ColorInput
                  size="xs"
                  value={doc.theme.accent}
                  onChange={(value) => patchTheme({ accent: value })}
                  aria-label={t("common.moreOptions")}
                  format="hex"
                  swatches={ACCENTS}
                  eyeDropperButtonProps={{ "aria-label": t("common.pickColor") }}
                />
                <SegmentedControl
                  size="xs"
                  value={doc.theme.pageSize}
                  onChange={(value) => patchTheme({ pageSize: value as ResumeTheme["pageSize"] })}
                  data={[
                    { value: "a4", label: "A4" },
                    { value: "letter", label: "Letter" },
                  ]}
                />
                <SegmentedControl
                  size="xs"
                  value={doc.theme.fontPair}
                  onChange={(value) => patchTheme({ fontPair: value as ResumeTheme["fontPair"] })}
                  data={[
                    { value: "sans", label: "Sans" },
                    { value: "serif", label: "Serif" },
                    { value: "mono", label: "Mono" },
                    { value: "mixed", label: "Mixed" },
                  ]}
                />
                <SegmentedControl
                  size="xs"
                  value={doc.theme.density}
                  onChange={(value) => patchTheme({ density: value as ResumeTheme["density"] })}
                  data={[
                    { value: "comfortable", label: "Comfort" },
                    { value: "compact", label: "Compact" },
                  ]}
                />
              </SimpleGrid>
              <Switch
                size="xs"
                mt="xs"
                label={t("profile.avatar")}
                checked={doc.theme.showAvatar}
                onChange={(event) => patchTheme({ showAvatar: event.currentTarget.checked })}
              />
            </section>

            <ScaledPreview doc={doc} />
          </div>
        ) : null}
      </div>

      {adding ? (
        <AddItemDialog
          section={adding}
          opened
          onClose={() => setAdding(null)}
          onSubmit={(input) => addItem(adding, input)}
        />
      ) : null}

      <ShareDialog resumeId={id} opened={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className={styles.centered}>
      <div>{children}</div>
    </div>
  );
}

function Panel({
  title,
  count,
  onAdd,
  addLabel,
  children,
}: {
  title: string;
  count?: number;
  onAdd?: () => void;
  addLabel?: string;
  children: ReactNode;
}) {
  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <div className={styles.panelHeading}>
          <h2 className={styles.panelTitle}>{title}</h2>
          {typeof count === "number" ? <span className={styles.count}>{count}</span> : null}
        </div>
        {onAdd ? (
          <Tooltip label={addLabel} withArrow>
            <ActionIcon variant="subtle" color="gray" onClick={onAdd} aria-label={addLabel}>
              <IconPlus size={17} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        ) : null}
      </div>
      <div className={styles.panelBody}>{children}</div>
    </section>
  );
}

function SaveState({ status, onRetry }: { status: SaveStatus; onRetry: () => void }) {
  const t = useMessages();
  if (status === "saving") {
    return (
      <span className={styles.saveStatus}>
        <Loader size={12} color="gray" />
        {t("common.saving")}
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className={styles.saveStatus} data-error="true">
        {t("common.saveFailed")}
        <button type="button" className={styles.retry} onClick={onRetry}>
          {t("common.retrySave")}
        </button>
      </span>
    );
  }
  return <span className={styles.saveStatus}>{t("editor.savedAll")}</span>;
}

function ScaledPreview({ doc }: { doc: ResumeDocument }) {
  const { ref, width } = useElementSize();
  const inner = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(1123);

  useEffect(() => {
    const node = inner.current;
    if (!node) return;
    const observer = new ResizeObserver(() => setHeight(node.offsetHeight));
    observer.observe(node);
    setHeight(node.offsetHeight);
    return () => observer.disconnect();
  }, []);

  const scale = width > 0 ? Math.min(1, width / PAGE_WIDTH) : 0;

  return (
    <div id="cv-print" ref={ref} className={styles.previewBox} style={{ height: height * scale }}>
      <div
        ref={inner}
        className="cv-scale"
        style={{
          width: PAGE_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          opacity: scale > 0 ? 1 : 0,
        }}
      >
        <div key={doc.templateId} className={styles.fade}>
          <CvRenderer doc={doc} />
        </div>
      </div>
    </div>
  );
}

export function EditorBadge({ children }: { children: ReactNode }) {
  return (
    <Badge variant="light" color="gray">
      {children}
    </Badge>
  );
}
